// Reception dashboard — wire-format mapper (RON-5469).
//
// Same split as the Visitor module: `ReceptionAPI.jsx` owns *which endpoint*, this file owns
// *what shape*. The API speaks snake_case with nested objects; every screen here reads a flat
// camelCase row, so a backend rename costs one line and a test fixture rather than reaching a
// component.
//
// Contract (READ-ONLY — the Laravel team's folder, never write to it):
//   D:\laragon\www\laravel-migration\docs\RON-5460\RON-5460_frontend-reception.md
//
// The unit-test fixtures are copied verbatim out of that document, so a change on their side
// fails a test here rather than a screen at a front desk.

import { DEFAULT_PER_PAGE, MAX_PER_PAGE, RECEPTION_FILTERS } from "./receptionConstants.js";

// Only keys the caller actually set.
const omitUndefined = (object) =>
  Object.entries(object).reduce((kept, [key, value]) => {
    if (value !== undefined) kept[key] = value;
    return kept;
  }, {});

// `HH:MM:SS` in, `HH:MM` on screen. Also tolerates a full datetime, because `checked_in_at` and
// `expected_out` are datetimes while `arrival_time` is a bare time.
const TIME_ONLY = /^\d{1,2}:\d{2}$/;

export const timeFromApi = (value) => {
  if (typeof value !== "string" || value === "") return undefined;

  const withoutDate = value.includes("T") ? value.split("T")[1] : value.split(" ").pop();
  const [hours, minutes] = String(withoutDate).split(":");
  const time = minutes === undefined ? withoutDate : `${hours}:${minutes}`;

  return TIME_ONLY.test(time) ? time : undefined;
};

// ── The board query (§3) ─────────────────────────────────────────────────────────────────────
//
// `site_id` is required on EVERY call (§2) — a board with no site is not a board, and the server
// refuses it. It is validated here so the failure is a readable error in development rather than
// a 422 at a front desk.
//
// `filter`, `q` and `date` COMBINE. They are three narrowing conditions on one query, not three
// passes — the doc calls out a shipped bug elsewhere where two filter passes fought each other.
export const boardQueryToApi = ({ SiteID, Date: date, Filter, Query, Page, PerPage } = {}) => {
  if (!SiteID) {
    throw new Error("Reception board requires a site_id — every reception call is scoped to one site.");
  }

  if (Filter != null && Filter !== "" && !RECEPTION_FILTERS.includes(Filter)) {
    // Refused rather than dropped. A dropped filter renders an unfiltered board that looks
    // filtered, and "0 expected" reads to a receptionist as a fact about their day.
    throw new Error(
      `Unknown reception filter "${Filter}". Use one of: ${RECEPTION_FILTERS.join(", ")}.`
    );
  }

  if (PerPage != null && (Number(PerPage) < 1 || Number(PerPage) > MAX_PER_PAGE)) {
    // The server refuses these too (422, never a silent correction). Catching it here keeps the
    // reason in words rather than as a status code.
    throw new Error(`per_page must be between 1 and ${MAX_PER_PAGE}; got ${PerPage}.`);
  }

  if (Page != null && Number(Page) < 1) {
    throw new Error(`page is 1-based; got ${Page}.`);
  }

  return omitUndefined({
    site_id: SiteID,
    date: date || undefined,
    filter: Filter || undefined,
    q: Query || undefined,
    page: Page ?? undefined,
    per_page: PerPage ?? undefined,
  });
};

// ── The walk-in body (§5) ────────────────────────────────────────────────────────────────────
//
// IT REGISTERS AND CHECKS THEM IN, IN ONE CALL. "A walk-in is standing at the desk — creating them
// `expected` and making the receptionist press a second button would misreport who is in the
// building for as long as it took. Don't build a second step."
//
// `name` and `host_id` are required: "somebody at a desk is there to see a person, and a visit
// with no host is one nobody can be told about or escort."
export const walkInToApi = ({
  SiteID,
  Name,
  HostID,
  Company,
  Email,
  Purpose,
  ExpectedOut,
  Date: date,
  IsVip,
  BuildingID,
  RequireHs,
  RequireNda,
} = {}) => {
  if (!SiteID) throw new Error("A walk-in needs a site_id.");
  if (!String(Name || "").trim()) throw new Error("A walk-in needs a name.");
  if (!HostID) throw new Error("A walk-in needs a host_id.");

  return omitUndefined({
    site_id: SiteID,
    name: String(Name).trim(),
    host_id: HostID,
    company: Company || undefined,
    // Optional, but validated when present — "a typo means the host is told about somebody the
    // system can never contact again".
    email: Email || undefined,
    purpose: Purpose || undefined,
    // A full datetime, unlike the invite module's bare `HH:MM`: reception stores it that way, and
    // a bare time has no meaning on a visit that could span days.
    expected_out: ExpectedOut && date ? `${date} ${ExpectedOut}:00` : undefined,
    is_vip: IsVip === undefined ? undefined : Boolean(IsVip),
    building_id: BuildingID || undefined,
    // STORED, AND READ BY NOTHING YET (A16). They record what the desk said was required, not
    // what anybody signed — see the note in `receptionConstants.js`. Sent as real booleans so the
    // intent is recorded truthfully even while nothing acts on it.
    require_hs: RequireHs === undefined ? undefined : Boolean(RequireHs),
    require_nda: RequireNda === undefined ? undefined : Boolean(RequireNda),
  });
};

// ── Sites (§2) ───────────────────────────────────────────────────────────────────────────────
//
// `GET /visitor/reception/sites` → `Records.items`. Active sites, this company only.
//
// NOTE: the real payload carries `address` and `timezone` but NO coordinates — the invite
// module's sites endpoint has `location`, this one does not. So the "Get directions" link only
// appears where a location happens to be present, which on this endpoint is never today. It is
// mapped anyway rather than removed: the field costs nothing and the link lights up on its own if
// they add one.
export const sitesFromApi = (records = {}) =>
  (records.items || []).map((site) => ({
    id: site.id,
    name: site.name,
    address: site.address ?? null,
    timezone: site.timezone ?? null,
    location:
      site.location?.lat != null && site.location?.lng != null ? site.location : null,
    // A12: whether the site HAS a lock PIN — never the PIN itself, which no endpoint returns.
    // `false` means the lock screen must not offer a box nobody can satisfy.
    receptionPinSet: site.reception_pin_set === true,
  }));

// ── Counts (§3) ──────────────────────────────────────────────────────────────────────────────
//
// ONE object, feeding both the five stat tiles and the seven filter tabs, so the two regions
// cannot disagree. They describe the whole day and site and do NOT move with `filter` or `q`.
export const countsFromApi = (counts = {}) => ({
  all: counts.all ?? 0,
  expected: counts.expected ?? 0,
  onSite: counts.on_site ?? 0,
  walkIns: counts.walk_ins ?? 0,
  vip: counts.vip ?? 0,
  contractors: counts.contractors ?? 0,
  overstay: counts.overstay ?? 0,
});

// ── Compliance (§4) ──────────────────────────────────────────────────────────────────────────
//
// `hs_acknowledged` and `nda_signed` are ABSENT rather than `false` until something writes them.
// That distinction has to survive the mapper, so the keys are copied ONLY when present — spreading
// a default of `false` here would destroy the very fact the backend went out of its way to keep.
const complianceFromApi = (compliance = {}) => {
  const mapped = {
    photoCaptured: compliance.photo_captured === true,
    badgeNumber: compliance.badge_number ?? null,
    badgeState: compliance.badge_state ?? null,
  };

  if ("hs_acknowledged" in compliance) mapped.hsAcknowledged = compliance.hs_acknowledged;
  if ("nda_signed" in compliance) mapped.ndaSigned = compliance.nda_signed;

  return mapped;
};

// ── A row (§4) — the same shape in the list and the detail panel ─────────────────────────────
export const rowFromApi = (api) => {
  if (!api) return null;

  return {
    id: api.id,
    bookingCode: api.booking_code,
    name: api.name,
    company: api.company,
    email: api.email,
    kind: api.kind,
    isVip: api.is_vip === true,
    isWalkIn: api.is_walk_in === true,
    status: api.status,

    // A FLAG on a row whose status is still `checked_in` — never a status of its own.
    isOverstay: api.is_overstay === true,
    // Minutes past `expected_out`, computed server-side so every device agrees.
    overstayBy: api.overstay_by ?? null,

    host: api.host,
    building: api.building,
    // The visitor's purpose; for a contractor this carries `work_description`, so one field
    // serves both.
    purpose: api.purpose,

    startDate: api.start_date,
    endDate: api.end_date ?? api.start_date,
    arrivalTime: timeFromApi(api.arrival_time),
    expectedOut: timeFromApi(api.expected_out),
    checkedInAt: timeFromApi(api.checked_in_at),
    checkedOutAt: timeFromApi(api.checked_out_at),

    // At least 1, and can be more: one contractor visit can cover a crew of six. The muster
    // counts people, not rows.
    personnel: api.personnel ?? 1,

    // `null` for a visitor and an object for a contractor — returned as null rather than omitted
    // so one shape binds.
    contractor: api.contractor
      ? {
          id: api.contractor.id,
          trade: api.contractor.trade,
          status: api.contractor.status,
          vettedUntil: api.contractor.vetted_until ?? null,
          isProvisional: api.contractor.is_provisional === true,
        }
      : null,

    compliance: complianceFromApi(api.compliance),

    // From the server. "Drive your buttons off them rather than inferring from `status` — the
    // rules include things a row cannot show you." Fail closed: absent reads as false.
    canCheckIn: api.can_check_in === true,
    canCheckOut: api.can_check_out === true,
  };
};

// ── The board (§3) ───────────────────────────────────────────────────────────────────────────
// The lock screen's two settings, carried on the board's first paint (§7b, A22).
//
// `inactivity_lock_minutes` is an ADMIN'S setting, 1–480, and the doc is blunt about it: "please
// stop hardcoding it … 30 is only what a company gets before it has saved its kiosk settings once
// — read the value rather than defaulting to it." So 30 is used only when the field is genuinely
// absent, which today means an API that has not shipped A22 yet.
//
// `reception_pin_set` also appears here, and this copy is the fresher one: since A22 a paired
// Android kiosk can change the staff PIN, so it can change without anyone touching this screen.
// Reading it off every board poll beats caching what the sites call said at login.
const DEFAULT_IDLE_LOCK_MINUTES = 30;

export const settingsFromApi = (settings) => ({
  idleLockMinutes: Number(settings?.inactivity_lock_minutes) || DEFAULT_IDLE_LOCK_MINUTES,
  // Undefined rather than false when absent, so the caller can fall back to the site's own flag
  // instead of being told there is no PIN.
  receptionPinSet:
    settings && "reception_pin_set" in settings ? settings.reception_pin_set === true : undefined,
});

export const boardFromApi = (records = {}) => ({
  settings: settingsFromApi(records.settings),
  // The server's clock at the read. Nothing is cached; this drives the "how fresh is this board"
  // line in the footer.
  asAt: records.as_at ?? null,
  date: records.date ?? null,
  // Returned so the TODAY pill does not have to compare clocks with the server.
  isToday: records.is_today === true,
  site: records.site ? { id: records.site.id, name: records.site.name } : null,

  counts: countsFromApi(records.counts),
  filter: records.filter ?? null,
  query: records.q ?? "",

  items: (records.items || []).map(rowFromApi),

  page: records.page ?? 1,
  perPage: records.per_page ?? DEFAULT_PER_PAGE,
  // The whole FILTERED set, not the page — it moves with filter/q/date, which is what
  // "showing 1-100 of 340 expected" needs. `counts` does not move, so the two are different on
  // purpose and both can be shown.
  total: records.total ?? 0,
  // `truncated` and `limit` are DEPRECATED and go after one release. Read this instead.
  hasMore: records.has_more === true,
});
